import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import StreamList from "Components/marketing/StreamList";
import { PLATFORM_PROOF, CALCULATOR_STORAGE_KEY } from "commons/earnings.constants";
import { getFromSessionStorage } from "helpers/sessionStorage.helper";
import {
  calculateEarnings,
  formatInrCompact,
  formatCount,
} from "services/earnings.service";

/**
 * Reassurance alongside the signup form.
 *
 * Signup is where a convinced partner turns into a filled-in form, and it's the
 * step most likely to be abandoned. If they came via the calculator we show the
 * number that brought them here — carried across in session storage, no backend
 * involved.
 */
function SignupRail({ members, bookingRate }) {
  const stored = getFromSessionStorage(CALCULATOR_STORAGE_KEY, "inputs");
  const effectiveMembers = members ?? stored?.members;
  const effectiveRate = bookingRate ?? stored?.bookingRate;

  const hasEstimate =
    Number.isFinite(effectiveMembers) && Number.isFinite(effectiveRate);

  const estimate = hasEstimate
    ? calculateEarnings({
        members: effectiveMembers,
        bookingRatePct: effectiveRate,
      })
    : null;

  return (
    <aside className="space-y-8">
      {estimate && (
        <div className="offset-card bg-card p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Your estimate
          </p>
          <p className="mt-3 font-display text-4xl font-bold leading-none tabular-nums text-[hsl(var(--brand-coral))]">
            {formatInrCompact(estimate.annual)}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            a year, with {formatCount(effectiveMembers)} members and{" "}
            {effectiveRate}% booking each month.
          </p>
          <Link
            to={`/calculator?members=${effectiveMembers}&rate=${effectiveRate}`}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            Adjust the numbers <ArrowRight size={14} />
          </Link>
        </div>
      )}

      <div>
        <h2 className="font-display text-xl font-semibold tracking-tight text-foreground">
          What you&apos;re signing up for
        </h2>
        <div className="mt-5">
          <StreamList compact />
        </div>
      </div>

      <div className="rounded-2xl border border-border/50 bg-card p-6">
        <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <ShieldCheck className="h-4 w-4 text-[hsl(var(--brand-lavender))]" />
          You still control everything that matters
        </p>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Your culture, your vibe, how you move, where you meet and what your
          community stands for. ShoUp simply removes the friction around it.
        </p>
      </div>

      <p className="text-sm text-muted-foreground">
        <span className="font-semibold text-[hsl(var(--brand-lavender))]">
          {PLATFORM_PROOF.communities} communities
        </span>{" "}
        and{" "}
        <span className="font-semibold text-[hsl(var(--brand-lavender))]">
          {PLATFORM_PROOF.membersLabel} members
        </span>{" "}
        are already on ShoUp. Free to list on, no lock-ins.
      </p>
    </aside>
  );
}

export default SignupRail;
